/**
 * IntegratedMenuFormExtras.jsx — 통합 메뉴 편집 창 조각 (2026-10-01, 시안대로 — 편집 창 파일을 짧게 두려고 분리)
 *
 *   CompanyPills  : 회사를 눌러 켜는 둥근 단추. 편집 중 회사를 **더하면** 경고 — 배정된 사람이 그 회사 자료도 보게 된다.
 *   GroupHint     : 사이드바 묶음(보여 주기만). 저장할 칸이 DB 에 없어(구조 변경 금지) 같은 기능의 일반 메뉴 묶음을 따라간다.
 *   AssigneeBox   : 배정 N명 + 업무/기능 배정으로 가는 길(누가 보는지는 그 화면에서 정한다).
 *   PairedBox     : 출퇴근 ↔ 출퇴근 달력 짝. 회사 묶음이 달라지면 짝이 풀린다는 경고.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { companyColor } from './integratedFeatureMeta';
import { findPairedAttendanceMenu } from '../attendancehub/integratedAttendanceTabs';
import { displayMenuName, integratedMenuPlace } from '../adminlayout/integratedScope';

const upper = (c) => String(c || '').trim().toUpperCase();
const sameSet = (a, b) => [...new Set(a.map(upper))].sort().join(',') === [...new Set(b.map(upper))].sort().join(',');

const boxStyle = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 12, padding: '12px 14px', background: 'var(--bg-subtle, #f8fafc)', marginBottom: 12 };
const boxTitle = { fontSize: 13, fontWeight: 900, color: 'var(--text, #111)' };
const boxText = { fontSize: 12, color: 'var(--text-muted, #64748b)', marginTop: 2 };
const warnStyle = { fontSize: 12, color: '#b45309', background: 'rgba(245,158,11,0.10)', borderRadius: 8, padding: '8px 10px', marginTop: 8 };

// 단추·경고 글자 = 작업 대상과 같은 회사 이름(2026-10-15 — 전엔 "SUNCREAM2 무기자차"처럼 코드+이름)
export function CompanyPills({ companies, selected, original, assignees, isEdit, onToggle }) {
  const added = isEdit ? selected.filter((c) => !original.map(upper).includes(upper(c))) : [];
  const nameOf = (code) => companies.find((x) => upper(x.code) === upper(code))?.name || code;
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {companies.map((c) => {
          const on = selected.includes(c.code);
          const col = companyColor(c.code);
          const name = c.name || c.code;
          return (
            <button
              key={c.code}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(c.code)}
              style={on
                ? { ...pillStyle, border: '1px solid transparent', background: col.bg, color: col.fg, fontWeight: 900 }
                : { ...pillStyle, border: '1px solid var(--border, #cbd5e1)', background: 'var(--bg-card, #fff)', color: 'var(--text-sub, #475569)' }}
            >
              {on ? name : `+ ${name}`}
            </button>
          );
        })}
      </div>
      {added.length > 0 && (
        <div style={warnStyle}>
          저장하면 배정된 {Number(assignees) || 0}명이 {added.map(nameOf).join('·')} 자료도 보게 됩니다.
        </div>
      )}
    </>
  );
}
const pillStyle = { height: 34, padding: '0 14px', borderRadius: 17, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };

export function GroupHint({ featureType, menuGroups }) {
  const { group } = integratedMenuPlace({ feature_type: featureType }, menuGroups);
  return (
    <div style={{ fontSize: 13, color: 'var(--text, #111)' }}>
      <b>{group}</b>
      <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted, #888)' }}>같은 기능의 일반 메뉴 묶음을 따라가요</span>
    </div>
  );
}

export function AssigneeBox({ count }) {
  return (
    <div style={{ ...boxStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div>
        <div style={boxTitle}>배정 {Number(count) || 0}명</div>
        <div style={boxText}>누가 이 메뉴를 볼 수 있는지는 업무/기능 배정에서 정해요</div>
      </div>
      <Link to="/admin/work-feature-assignments" style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>
        배정하러 가기
      </Link>
    </div>
  );
}

/** 짝 상자 — 달력 메뉴면 짝 출퇴근 메뉴, 출퇴근 메뉴면 짝 달력 메뉴들. 짝이 없으면 아무것도 안 그린다. */
export function PairedBox({ editing, menus, selected }) {
  if (!editing || !Array.isArray(menus)) return null;
  const partner = findPairedAttendanceMenu(editing, menus);
  const calendars = editing.feature_type === 'attendance'
    ? menus.filter((m) => findPairedAttendanceMenu(m, menus)?.id === editing.id)
    : [];
  if (!partner && calendars.length === 0) return null;
  const broken = !sameSet(selected, editing.company_codes || []);
  return (
    <div style={boxStyle}>
      <div style={boxTitle}>같이 묶인 메뉴</div>
      {partner && (
        <div style={boxText}>
          {displayMenuName(partner.name)}(#{partner.id}) 안 [달력] 탭으로 합쳐져 이 메뉴는 사이드바에서 숨김
        </div>
      )}
      {calendars.map((m) => (
        <div key={m.id} style={boxText}>
          {displayMenuName(m.name)}(#{m.id}) — 이 메뉴의 [달력] 탭으로 합쳐져 사이드바에선 숨김
        </div>
      ))}
      {broken && (
        <div style={warnStyle}>회사가 달라지면 짝이 풀려 달력 메뉴가 사이드바에 따로 보여요. 짝 메뉴의 회사도 같이 바꿔 주세요.</div>
      )}
    </div>
  );
}
